<template>
  <div class="rm-drawer-step">
    <div class="rm-drawer-scroll">
      <section class="drawer-section">
        <h3 class="drawer-section-title">
          {{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.SUB_TITLE') }}
        </h3>

        <v-radio-group v-model="selectedOffer" class="repairer-choice-group" hide-details>
          <template
            :key="repairerOffer.technical_id"
            v-for="repairerOffer of currentRepairFile?.repairer_estimate
              ?.repairer_estimate_option_list"
          >
            <v-card-secondary
              class="repairer-choice-card pa-3"
              :class="{
                'secondary-card--selected':
                  selectedOffer?.technical_id === repairerOffer.technical_id
              }"
              @click="selectedOffer = repairerOffer"
            >
              <v-radio :value="repairerOffer" hide-details>
                <template v-slot:label>
                  <div class="secondary-card-row w-100">
                    <p class="drawer-offer-label">
                      {{ $t('COMMON.PRODUCT_END_STATUS.' + repairerOffer.offer_type) }}
                    </p>
                    <p class="ml-auto drawer-offer-price">
                      {{ Number(repairerOffer.total_cost).toFixed(2) }} {{ $t('COMMON.EURO') }}
                    </p>
                  </div>
                </template>
              </v-radio>

              <div
                class="repairer-offer-detail"
                v-if="
                  selectedOffer?.technical_id === repairerOffer?.technical_id &&
                  !initialZeroCostIds.includes(repairerOffer.technical_id)
                "
              >
                <div class="repairer-offer-detail-content">
                  <div class="repairer-choice-metrics">
                    <div class="secondary-card--soft repairer-choice-metric">
                      <p class="repairer-choice-metric-label">
                        {{
                          $t(
                            isReplacementEstimateOffer(repairerOffer)
                              ? 'COMMON.COST'
                              : 'ESTIMATE_REPAIRER_CLIENT_CHOICE.TRANSPORT_COST'
                          )
                        }}
                      </p>
                      <p class="repairer-choice-metric-value">
                        {{
                          (
                            repairerOffer.estimate_cost +
                            repairerOffer.transport_cost +
                            repairerOffer.labor_cost
                          ).toFixed(2)
                        }}
                        {{ $t('COMMON.EURO') }}
                      </p>
                    </div>
                    <div class="secondary-card--soft repairer-choice-metric">
                      <p class="repairer-choice-metric-label">
                        {{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.MARGIN') }}
                      </p>
                      <p class="repairer-choice-metric-value">
                        {{ repairerOffer.margin }} {{ $t('COMMON.PERCENT') }}
                      </p>
                    </div>
                  </div>

                  <div
                    class="drawer-actions repairer-offer-actions"
                    v-if="!isEditingOffer(repairerOffer)"
                  >
                    <v-btn-icon
                      :title="$t('ESTIMATE_REPAIRER_CLIENT_CHOICE.MODIFICATION_PRICE')"
                      icon="mdi-pencil-outline"
                      size="small"
                      @click.stop="startPriceEdit(repairerOffer)"
                    />
                    <v-btn-icon
                      :title="$t('ESTIMATE_REPAIRER_CLIENT_CHOICE.MODIFICATION_MARGIN')"
                      icon="mdi-percent-outline"
                      size="small"
                      @click.stop="startMarginEdit(repairerOffer)"
                    />
                  </div>
                </div>
              </div>
            </v-card-secondary>

            <v-form-card
              ref="formUpdateEstimateRepairerOption"
              v-model="isUpdateOptionFormValid"
              v-if="isEditingOffer(repairerOffer)"
              @submit.prevent="saveEditedOffer(repairerOffer)"
            >
              <div class="drawer-form-header">
                <p class="drawer-form-title">
                  {{ editPanelTitle }}
                </p>
                <v-btn-icon
                  class="drawer-form-close"
                  icon="mdi-close"
                  size="small"
                  @click="cancelEdit"
                />
              </div>

              <div class="drawer-form-field">
                <p class="custom-label" required="true">{{ editFieldLabel }}</p>
                <v-text-field
                  v-model.number="editingValue"
                  type="number"
                  :rules="editingField === 'price' ? priceRules : marginRules"
                  hide-details="auto"
                  :placeholder="
                    editingField === 'price'
                      ? $t('COMMON.PLACEHOLDER.PRICE')
                      : $t('COMMON.PLACEHOLDER.MARGIN')
                  "
                >
                  <template v-slot:append-inner>
                    <span class="drawer-field-suffix">
                      {{ editingField === 'price' ? $t('COMMON.EURO') : $t('COMMON.PERCENT') }}
                    </span>
                  </template>
                </v-text-field>
              </div>

              <v-btn-secondary
                class="w-100"
                :disabled="!isUpdateOptionFormValid || loading"
                :loading="loading"
                prepend-icon="mdi-check"
                variant="tonal"
                type="submit"
              >
                {{ editSaveLabel }}
              </v-btn-secondary>
            </v-form-card>
          </template>
        </v-radio-group>
      </section>

      <section class="drawer-section">
        <h3 class="drawer-section-title">
          {{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.SEND_ESTIMATE_AGAIN') }}
        </h3>
        <v-form v-model="isEmailFormValid" class="drawer-email-form">
          <div class="drawer-field">
            <v-text-field
              required
              v-model="email"
              :rules="[...requiredRules, ...emailRules]"
              hide-details="auto"
              :placeholder="$t('ESTIMATE_REPAIRER_CLIENT_CHOICE.PLACEHOLDER.SEND_ESTIMATE_AGAIN')"
              prepend-inner-icon="mdi-email-outline"
            />
          </div>

          <v-btn-secondary
            class="w-100"
            :disabled="loading || !isEmailFormValid || hasSelectedEstimateOption"
            :loading="loading"
            prepend-icon="mdi-send-outline"
            variant="tonal"
            @click="sendClientEmail"
          >
            {{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.BUTTON.SEND_EMAIL') }}
          </v-btn-secondary>
        </v-form>
      </section>
    </div>
  </div>
</template>

<script>
import Validators from '@/utils/validators'
import { isReplacementEstimateOffer } from '@/utils/repairFileUtils'
import EstimateRepairerService from '@/services/EstimateRepairerService'
import { displayToast, handleResponseError } from '@/utils/toastUtils'

export default {
  props: {
    currentRepairFile: Object
  },
  data() {
    return {
      loading: false,
      error: null,
      isUpdateOptionFormValid: false,
      repairer_estimate_option_list: null,
      selectedOffer: null,
      editingOfferId: null,
      editingField: null,
      editingValue: null,
      initialZeroCostIds: [],

      priceRules: Validators.numberRules,
      marginRules: Validators.marginRules,
      requiredRules: Validators.requiredRules,
      emailRules: Validators.emailRules,

      email: null,
      isEmailFormValid: false
    }
  },
  created() {
    this.patchData()

    const options = this.currentRepairFile.repairer_estimate.repairer_estimate_option_list

    this.repairer_estimate_option_list = JSON.parse(JSON.stringify(options))

    this.initialZeroCostIds = options
      .filter((option) => Number(option.total_cost) === 0)
      .map((option) => option.technical_id)
  },
  computed: {
    footerState() {
      return {
        disabled: this.loading || !this.selectedOffer,
        loading: this.loading
      }
    },
    hasSelectedEstimateOption() {
      return this.currentRepairFile?.repairer_estimate?.repairer_estimate_option_list?.some(
        (option) => option.is_selected === true
      )
    },
    editPanelTitle() {
      if (this.editingField === 'price') {
        return this.$t('ESTIMATE_REPAIRER_CLIENT_CHOICE.MODIFICATION_PRICE')
      }
      return this.$t('ESTIMATE_REPAIRER_CLIENT_CHOICE.MODIFICATION_MARGIN')
    },
    editFieldLabel() {
      if (this.editingField === 'price') {
        return this.$t('ESTIMATE_REPAIRER_CLIENT_CHOICE.MODIFICATION_PRICE')
      }
      return this.$t('ESTIMATE_REPAIRER_CLIENT_CHOICE.MARGIN')
    },
    editSaveLabel() {
      if (this.editingField === 'price') {
        return this.$t('ESTIMATE_REPAIRER_CLIENT_CHOICE.SAVE_PRICE')
      }
      return this.$t('ESTIMATE_REPAIRER_CLIENT_CHOICE.SAVE_MARGIN')
    }
  },
  watch: {
    footerState: {
      handler() {
        this.emitFooterState()
      },
      deep: true
    }
  },
  mounted() {
    this.emitFooterState()
  },
  methods: {
    isReplacementEstimateOffer,
    emitFooterState() {
      this.$emit('updateFooterState', this.footerState)
    },
    async submit() {
      this.selectedOffer.is_selected = true
      this.$emit('openEstimateRepairerClientChoiceValidation')
    },
    isEditingOffer(repairerOffer) {
      return this.editingOfferId === repairerOffer.technical_id
    },
    startPriceEdit(repairerOffer) {
      this.editingOfferId = repairerOffer.technical_id
      this.editingField = 'price'
      this.editingValue = Number(repairerOffer.total_cost)
    },
    startMarginEdit(repairerOffer) {
      this.editingOfferId = repairerOffer.technical_id
      this.editingField = 'margin'
      this.editingValue = Number(repairerOffer.margin)
    },
    cancelEdit() {
      this.editingOfferId = null
      this.editingField = null
      this.editingValue = null
      this.isUpdateOptionFormValid = false
    },
    saveEditedOffer(repairerOffer) {
      if (!this.isUpdateOptionFormValid) {
        return
      }

      if (this.editingField === 'price') {
        this.updateRepairerEstimateOptionFromPrice(repairerOffer)
      } else {
        this.updateRepairerEstimateOptionFromMargin(repairerOffer)
      }
      this.cancelEdit()
    },
    updateRepairerEstimateOptionFromMargin(repairerEstimateOption) {
      repairerEstimateOption.margin = Number(this.editingValue)
      repairerEstimateOption.total_cost =
        (repairerEstimateOption.estimate_cost +
          repairerEstimateOption.transport_cost +
          repairerEstimateOption.labor_cost) /
        (1 - repairerEstimateOption.margin / 100)
    },
    updateRepairerEstimateOptionFromPrice(repairerEstimateOption) {
      repairerEstimateOption.total_cost = parseFloat(this.editingValue)
      const newMargin =
        (100 *
          (repairerEstimateOption.total_cost -
            (repairerEstimateOption.estimate_cost +
              repairerEstimateOption.transport_cost +
              repairerEstimateOption.labor_cost))) /
        repairerEstimateOption.total_cost
      repairerEstimateOption.margin = newMargin.toFixed(0)
    },
    patchData() {
      this.email = this.currentRepairFile?.email
    },
    async sendClientEmail() {
      if (this.hasSelectedEstimateOption) {
        displayToast(this.$t('COMMON.NOT_AUTHORIZED_ACTION_MESSAGE'), 'warning')
        return
      }
      this.loading = true
      try {
        const data = {
          repair_file_id: this.currentRepairFile.technical_id,
          contact_email: this.email
        }
        await EstimateRepairerService.sendNewRepairFileRepairerEstimateToClient(data)
        displayToast(this.$t('ESTIMATE_REPAIRER_DETAIL.MODAL.SEND_CLIENT_EMAIL'), 'success')
        this.$emit('updateCurrentRepairFile')
        this.$emit('closeNewAction')
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.repairer-choice-group {
  width: 100%;
}

.repairer-choice-group :deep(.v-selection-control-group) {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.repairer-choice-card :deep(.v-selection-control) {
  align-items: center;
  min-height: 32px;
}

.repairer-choice-card :deep(.v-label) {
  opacity: 1;
  width: 100%;
}

.repairer-choice-card .drawer-actions {
  justify-content: flex-end;
}

.repairer-offer-actions {
  flex-wrap: nowrap;
  padding-inline: 12px;
}

@media (max-width: 420px) {
  .repairer-offer-actions {
    justify-content: flex-end;
  }
}
</style>
